import { left } from "@popperjs/core";
import { resource } from "../objects/resource";
import { DirectionalHint, ITooltipHostStyles, TooltipDelay, TooltipHost } from "@fluentui/react";

interface M365CardProps {
  ownedResource: resource;
}

/**
 * M365Card component for displaying resource information
 */
export function M365Card(props: M365CardProps) {
  const hostStyles: Partial<ITooltipHostStyles> = { 
    root: { display: "inline-block", width: "100%" } 
  };

  // Common label styles
  const labelStyle: React.CSSProperties = {
    fontWeight: "bold",
    textAlign: "right",
    paddingRight: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  };

  const inputStyle: React.CSSProperties = {
    width: "100%"
  };

  const rowStyle: React.CSSProperties = {
    marginBottom: "12px",
    alignItems: "center"
  };

  return (
    <div>
      <br></br>
          <table className="table table-bordered">
      <tbody>
        <tr style={{ backgroundColor: "white", textAlign: "justify" }}>
          <td>
            <div style={{ padding: "10px" }}>
      <div>
        <br />
        
        {/* Display Name & Type */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Display Name:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={props.ownedResource.displayName}
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Type:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.resourceType === "CSPO"
                  ? "SPO"
                  : props.ownedResource.resourceType
              }
              disabled
            />
          </div>
        </div>

        {/* Last Activity Date & Sharing Capability */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Last Activity:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                new Date(props.ownedResource.lastUpdatedFormatted).getFullYear() < 2000
                  ? "Inactive for 5+ years"
                  : new Date(props.ownedResource.lastUpdatedFormatted).toDateString()
              }
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Sharing:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.sensitivityLabel === ""
                  ? "Not Set"
                  : props.ownedResource.sensitivityLabel
              }
              disabled
            />
          </div>
        </div>

        {/* Privacy & AGS Managed */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Privacy:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={props.ownedResource.privacy}
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              AGS Managed:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={props.ownedResource.agsTracked ? "Yes" : "No"}
              disabled
            />
          </div>
        </div>

        {/* Mailbox Status & Address */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Mailbox Status:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={props.ownedResource.mailboxEnabled ? "Enabled" : "Disabled"}
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Mailbox Address:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.mailboxEnabled 
                  ? props.ownedResource.mailbox 
                  : "Not Enabled"
              }
              disabled
            />
          </div>
        </div>

        {/* Owner Count & External Members */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Owners:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={props.ownedResource.owners.length}
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              External Members:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.externalMembersCount === null || 
                isNaN(props.ownedResource.externalMembersCount) 
                  ? 0 
                  : props.ownedResource.externalMembersCount
              }
              disabled
            />
          </div>
        </div>

        {/* Member Count & Last Renewed */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Members:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.membersCount === null || 
                isNaN(props.ownedResource.membersCount) 
                  ? 0 
                  : props.ownedResource.membersCount
              }
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Last Renewed:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <TooltipHost
              content={
                props.ownedResource.renewActivityLog?.actingUser === "Not Renewed"
                  ? "This resource has not been renewed yet"
                  : `Renewed on ${props.ownedResource.renewActivityLog?.activityDateTime}`
              }
              delay={TooltipDelay.zero}
              directionalHint={DirectionalHint.bottomCenter}
              styles={hostStyles}
            >
              <input
                type="text"
                className="form-control"
                style={inputStyle}
                value={props.ownedResource.renewActivityLog?.actingUser}
                disabled
              />
            </TooltipHost>
          </div>
        </div>
        
        <br />
      </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    </div>
  );
}

export default M365Card;